import { lazy, Suspense, useEffect } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { MobileApp } from "@/components/mobile/MobileApp";
import { TopBar } from "@/components/TopBar";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { HomeHero } from "@/components/home/HomeHero";
import { PersonalizationPrompt } from "@/components/personalization/PersonalizationPrompt";
import { Seo } from "@/components/seo/Seo";
import { organizationSchema, webSiteSchema } from "@/lib/seo";

// Below-the-fold homepage content is code-split so the initial JS bundle stays
// small and first paint happens sooner on mobile networks.
const HomeSections = lazy(() =>
  import("@/components/home/HomeSections").then((m) => ({ default: m.HomeSections })),
);

const HomeSectionsSkeleton = () => (
  <div className="space-y-8 py-10" aria-label="Loading page content">
    <div className="ds-container">
      <div className="h-8 w-48 animate-pulse rounded bg-muted" />
      <div className="mt-4 h-12 w-3/4 animate-pulse rounded bg-muted" />
      <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-40 animate-pulse rounded-xl bg-muted" />
        ))}
      </div>
    </div>
    <div className="ds-container">
      <div className="h-64 animate-pulse rounded-xl bg-muted" />
    </div>
  </div>
);

const Index = () => {
  const isMobile = useIsMobile();


  useEffect(() => {
    if (isMobile) return;
    // Content must be fully legible without animation.
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("opacity-100", "translate-y-0");
            entry.target.classList.remove("opacity-0", "translate-y-6");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08, rootMargin: "80px" },
    );

    document.querySelectorAll(".reveal").forEach((el) => {
      el.classList.add("opacity-0", "translate-y-6", "transition-all", "duration-700", "will-change-transform");
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, [isMobile]);

  return (
    <div className="min-h-screen">
      <Seo
        title="PathFree Technologies | Intelligent Resuscitation Infrastructure"
        description="PathFree Technologies Corporation, Irvine CA, builds Intelligent Resuscitation Infrastructure: readiness, clinical response, documentation, data, and continuous improvement in one ecosystem."
        path="/"
        jsonLd={[organizationSchema(), webSiteSchema()]}
      />

      {isMobile ? (
        <MobileApp />
      ) : (
        <>
            <TopBar />
            <Header />
            <main>
              <HomeHero />
              <PersonalizationPrompt />
              <Suspense fallback={<HomeSectionsSkeleton />}>
                <HomeSections />
              </Suspense>
            </main>
            <Footer />
        </>
      )}
    </div>
  );
};

export default Index;
